/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import { useParams } from 'react-router-dom'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import { getAsset, unlistAsset } from '@/services/assets'
import { formatEmptyValue } from '@/utils/assets'
import { PDS_ASSET_ID } from '@/configs/strings'
import ReturnButton from '@/components/buttons/navigation/ReturnButton'
import ButtonBar from './components/ButtonBar'
import AssetModal from './components/AssetModal'
import BodyModalMetadata from './components/BodyModalMetadata'
import FooterModalMetadata from './components/FooterModalMetadata'
import BodySetCover from './components/BodySetCover'
import { defaultThumbnailUrl } from '@/configs/urls'
import DefaultProcess from '@/components/process/DefaultProcess'
import BuildLicenseTerms from './components/BuildLicenseListingTerms'
import {
  getBuildLicenseListingTerms,
  postBuildLicenseListingTerms,
  putBuildLicenseListingTerms
} from '@/services/build-license-listing-terms'
import { useAuth0 } from '@auth0/auth0-react'
import DefaultDetailsItem from '@/components/items/DefaultDetailsItem'
import ListedUnlistedTag from '../components/ListedUnlistedTag'
import RevenueBreakdown from '../components/RevenueBreakdown'
import { RETURN_YOUR_ASSETS } from '@/configs/strings/your-assets'
import { useResponsive } from '@/hooks/useResponsive'
import { Flex } from '@chakra-ui/react'
import { publicBucketBaseUrl } from '@/configs/env-vars'
import AssetLicenseHistory from './components/AssetLicenseHistory'

const Asset = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [modalIsVisible, setModalIsVisible] = useState(false)
  const [modalConfig, setModalConfig] = useState()
  const [showBuildLicense, setShowBuildLicense] = useState(false)
  const [showRevenue, setShowRevenue] = useState(false)
  const [activeModal, setActiveModal] = useState(null)
  const [hasErrorForm, setHasErrorForm] = useState(false)
  const { assetId } = useParams()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const {
    itemsResponse,
    loading,
    reloadItems
  } = useFetchItemsWithParamsV2({
    fetchService: getAsset,
    fetchParams: { id: assetId }
  })
  const {
    itemsResponse: buildLicenseListingTerms,
    loading: loadingBuildLicenseListingTerms,
    reloadItems: reloadTerms
  } = useFetchItemsWithParamsV2({
    fetchService: getBuildLicenseListingTerms,
    fetchParams: { id: assetId }
  })

  const formatItemsArray = (items) => {
    if (!items) return null

    const currentItems = items.map(item => item.name)
    return currentItems.join(', ')
  }

  const onCloseModal = () => {
    setModalIsVisible(false)
    setModalConfig(null)
    setActiveModal(null)
  }

  const openModal = (modalType) => {
    const modalConfigs = {
      metadata: {
        title: `${itemsResponse?.title} Metadata`,
        bodyModal: <BodyModalMetadata metadata={itemsResponse?.metadata} />,
        footerModal: <FooterModalMetadata onClose={onCloseModal} />
      },
      setCover: {
        title: 'Set a Asset cover',
        bodyModal: (
          <DefaultProcess
            startComponent={(props) => (
              <BodySetCover
                urlImage={assetCover && coverUrl}
                id={assetId}
                reloadItems={reloadItems}
                onClose={onCloseModal}
                {...props}
              />
            )}
          />
        )
      }
    }

    const currentConfig = modalConfigs[modalType]
    if (!currentConfig) return null

    setModalIsVisible(true)
    setModalConfig(currentConfig)
    setActiveModal(modalType)
  }

  const handleListAssetsClick = () => {
    if (showBuildLicense) {
      setShowBuildLicense(false)
    } else if (showRevenue) {
      setShowRevenue(false)
      setShowBuildLicense(true)
    } else {
      setShowBuildLicense(true)
    }
  }

  const handleCloseBuildLicense = () => {
    setShowBuildLicense(false)
  }

  const handleRevenueClick = () => {
    if (showRevenue) {
      setShowRevenue(false)
    } else if (showBuildLicense) {
      setShowBuildLicense(false)
      setShowRevenue(true)
    } else {
      setShowRevenue(true)
    }
  }

  const onUnlistAsset = async () => {
    setIsLoading(true)
    setHasErrorForm(false)

    try {
      await unlistAsset(assetId, getAccessToken)

      reloadItems()
    } catch (error) {
      setHasErrorForm(true)
    }

    setIsLoading(false)
  }

  const onSubmitBuildLicenseListingTerms = async ({
    data
  }) => {
    setIsLoading(true)
    setHasErrorForm(false)

    if (buildLicenseListingTerms?.id) {
      try {
        await putBuildLicenseListingTerms(
          buildLicenseListingTerms.id,
          { ...data, assetId },
          getAccessToken
        )

        await reloadTerms()
        await reloadItems()
      } catch (error) {
        setHasErrorForm(true)
      }

      setIsLoading(false)
    } else {
      try {
        await postBuildLicenseListingTerms(
          { ...data, assetId },
          getAccessToken
        )

        await reloadTerms()
        await reloadItems()
      } catch (error) {
        setHasErrorForm(true)
      }

      setIsLoading(false)
    }
  }

  const assetCover = itemsResponse?.coverUrl
  const coverUrl = `${publicBucketBaseUrl}/${assetCover}`
  const currentCoverUrl = assetCover
    ? coverUrl
    : defaultThumbnailUrl
  const { flexDirection, flexGap } = useResponsive()

  return (
    <RegularTemplate
      loadingContent={loading}
      itemNotFound={!itemsResponse}
    >
      {!loading && !loadingBuildLicenseListingTerms && itemsResponse && (
        <>
          { modalIsVisible && modalConfig && (
            <AssetModal
              title={modalConfig.title}
              body={modalConfig?.bodyModal}
              footer={modalConfig?.footerModal}
              onClose={onCloseModal}
            />
          )}
          <ReturnButton
            href="/"
            label={RETURN_YOUR_ASSETS}
          />

          <header className='mb-3'>
            <div className='is-flex items-center gap-2'>
              <h1 id='title'>{itemsResponse?.title}</h1>
              <ListedUnlistedTag listed={itemsResponse?.listed} />
            </div>
            <div className='is-flex'>
              <p>by {formatEmptyValue(formatItemsArray(itemsResponse?.creators))}</p>
            </div>
          </header>

          <ButtonBar
            filename={itemsResponse.filename}
            openModal={openModal}
            onListAssetsClick={handleListAssetsClick}
            onRevenueClick={handleRevenueClick}
            showBuildLicense={showBuildLicense}
            showRevenue={showRevenue}
            modalIsVisible={modalIsVisible}
            activeModal={activeModal}
          />

          <Flex
            flexDirection={flexDirection}
            gap={flexGap}
          >
            <DefaultDetailsItem
              imageUrl={`${currentCoverUrl}?v=${Date.now()}`}
              details={[
                { label: 'Title', value: itemsResponse.title },
                { label: 'Creators', value: formatEmptyValue(formatItemsArray(itemsResponse.creators)) },
                { label: 'Publisher', value: formatEmptyValue(formatItemsArray(itemsResponse.publishers)) },
                { label: PDS_ASSET_ID, value: formatEmptyValue(formatEmptyValue(itemsResponse.assetId)) },
                { label: 'File Type', value: itemsResponse.fileType },
                { label: 'Unique Identifier', value: itemsResponse.uniqueIdentifier },
                { label: 'Tokens', value: itemsResponse.tokens || '0' }
              ]}
            />
            <div className="grow">
              { showBuildLicense
                ? <BuildLicenseTerms
                    asset={itemsResponse}
                    onSubmit={onSubmitBuildLicenseListingTerms}
                    buildLicenseListingTerms={buildLicenseListingTerms}
                    onClose={handleCloseBuildLicense}
                    onUnlist={onUnlistAsset}
                    isLoading={isLoading}
                    hasAlert={hasErrorForm}
                    wrapperClassName="mb-5"
                  />
                : showRevenue
                  ? <RevenueBreakdown assetId={assetId} />
                  : null
              }
              <AssetLicenseHistory
                assetLicenses={itemsResponse?.licenseHistory}
              />
            </div>
          </Flex>
          <div>
          </div>
        </>
      )}
    </RegularTemplate>
  )
}

export default Asset
